import React, { useState, useEffect, useMemo } from 'react';
import { getService } from '@Client/runner.hooks/useService';
import {
  GridColumn as Column,
  GridCustomCellProps
} from '@progress/kendo-react-grid';
import { TagGroupEditModal } from './Modals/TagGroupEditModal/TagGroupEditModal';
import { TagGroupDeleteModal } from './Modals/TagGroupDeleteModal/TagGroupDeleteModal';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { EmptyGuid } from '@Shared.Angular/@types/guid';
import {
  CompositeFilterDescriptor,
  SortDescriptor
} from '@progress/kendo-data-query';
import ITagDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagDetail';
import Chip from '../../../../../Flowingly.Shared.Angular/src/flowingly.components/Chip/Chip';
import {
  KendoReactGrid,
  GridCustomFilterCellProps
} from '@Shared.Angular/kendo.react/KendoReactGrid';
import ActionButtons from '@Client/runner.library/ActionButtons';
import GridAutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/GridAutoCompleteFilter';

const emptyTag = {
  id: EmptyGuid,
  name: '',
  description: '',
  color: '',
  tags: []
};

const SetupTagGroups = () => {
  const tagGroupApiService = getService('tagGroupApiService');
  const permissionsService = getService('permissionsService');
  const appInsightsService = getService('appInsightsService');
  const pubSubService = getService('pubsubService');

  const [filter, setFilter] = useState<CompositeFilterDescriptor>(null);
  const [tagGroups, setTagGroups] = useState<ITagGroupDetail[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [tagFilterValue, setTagFilterValue] = useState<string>('');
  const [selectedTagGroup, setSelectedTagGroup] =
    useState<ITagGroupDetail>(emptyTag);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [hasTagInUse, setHasTagInUse] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    refreshData();

    const subscriberId = 'setup-tag-groups';
    pubSubService.subscribe(
      'SETUP_TAGGROUP_ADD_CLICKED',
      OpenAddTagGroupModal,
      subscriberId
    );
    pubSubService.subscribeLate(
      'TAG_GROUP_CREATED',
      getUpdatedTagGroups,
      subscriberId
    );
    pubSubService.subscribeLate(
      'TAG_GROUP_UPDATED',
      getUpdatedTagGroups,
      subscriberId
    );
    pubSubService.subscribeLate(
      'TAG_GROUP_DELETED',
      getUpdatedTagGroups,
      subscriberId
    );
    return () => {
      pubSubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const getUpdatedTagGroups = async () => {
    const newTagGroups = await tagGroupApiService.getAllTagGroups();
    setTagGroups(newTagGroups);
    setIsDataLoaded(true);
  };

  const refreshData = async (ignoreCache = false) => {
    appInsightsService.startEventTimer('setupTaggroupsLoaded');
    const newTagGroups = await tagGroupApiService.getAllTagGroups(ignoreCache);
    setTagGroups(newTagGroups);
    setIsDataLoaded(true);
  };

  const OpenAddTagGroupModal = () => {
    setSelectedTagGroup(emptyTag);
    setModalTitle('Add Tag Group');
    setEditOpen(true);
  };

  const editTagGroup = async (selectedTagGroup) => {
    const tagGroup = await tagGroupApiService.getById(selectedTagGroup.id);
    setSelectedTagGroup(tagGroup);
    setModalTitle('Edit Tag Group');
    setEditOpen(true);
  };

  const deleteTagGroup = async (selectedTagGroup) => {
    appInsightsService.startEventTimer('setupTaggroupsDeleteWarning');
    setSelectedTagGroup(selectedTagGroup);
    const response = await tagGroupApiService.getDeleteWarnings(
      selectedTagGroup.id
    );
    setHasTagInUse(response.hasTagInUse);
    setDeleteOpen(true);
  };

  const handleSaveSuccess = async () => {
    await refreshData(true);
    closeEditModal();
  };

  const closeEditModal = () => {
    setEditOpen(false);
  };

  const handleDeleteSuccess = async () => {
    await refreshData(true);
    setDeleteOpen(false);
  };

  const generateActionsCell = (props: GridCustomCellProps) => {
    let editHandler: () => void;
    let deleteHandler: () => void;
    const hasUpdatePermission = permissionsService.currentUserHasPermission(
      FlowinglyPermissions.SETUP_TAG_UPDATE
    );

    if (hasUpdatePermission) {
      const tagGroup: ITagGroupDetail = props.dataItem;
      editHandler = () => editTagGroup(tagGroup);
      deleteHandler = () => deleteTagGroup(tagGroup);
    }
    return (
      <td {...props.tdProps}>
        <ActionButtons
          showButtons={{
            edit: true,
            delete: true
          }}
          onEditClick={editHandler}
          onDeleteClick={deleteHandler}
        />
      </td>
    );
  };

  const TagsCell = (props: {
    dataItem: { tags: ITagDetail[]; color: string };
  }) => {
    const tags = props.dataItem.tags || [];
    return (
      <td>
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {tags.map((tag) => (
            <div key={tag.id} data-testid={`tag-group-tag-${tag.name}`}>
              <Chip name={tag.name} color={props.dataItem.color} />
            </div>
          ))}
        </div>
      </td>
    );
  };

  const NameFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(
        props,
        tagGroups.map((tg) => tg.name),
        'Search by Tag Group'
      ),
    [tagGroups]
  );

  const TagFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(
        props,
        tagGroups.flatMap((p) => p.tags.map((tag) => tag.name)),
        'Search by Tag',
        setTagFilterValue
      ),
    [tagGroups]
  );

  const initialSort = useMemo(
    () => [{ field: 'name', dir: 'asc' } as SortDescriptor],
    []
  );

  const getFilteredTagGroups = () => {
    if (!tagFilterValue) {
      return tagGroups;
    }
    return tagGroups.filter((group) =>
      group.tags.some((tag) => tag.name === tagFilterValue)
    );
  };

  if (!isDataLoaded) {
    return <div className="setup-tag-groups-container" />;
  }

  return (
    <div className="setup-tag-groups-container">
      <KendoReactGrid
        id="setup-tags-grid"
        autoProcessData={{
          filter: true,
          search: true,
          sort: true,
          group: false,
          page: true
        }}
        data={getFilteredTagGroups()}
        dataItemKey="id"
        defaultSort={initialSort}
        defaultTake={25}
        filter={filter}
        filterable={true}
        onFilterChange={(a) => {
          setFilter(a.filter);
        }}
        manuallyFilteredFieldNames={['tags']}
        pageable={{
          buttonCount: 10,
          info: true,
          previousNext: true
        }}
        sortable={true}
      >
        <Column
          field="name"
          title="Tag Group"
          width="250"
          className="column-name"
          cells={{ filterCell: NameFilterCellMemo }}
        />
        <Column
          field="description"
          title="Description"
          width="350"
          filterable={false}
          className="column-description"
        />
        <Column field="owner" title="Owner" width="200" filterable={false} />
        <Column
          field="numberOfFlowModelsUsing"
          title="Flow Models"
          width="200"
          filterable={false}
          className="column-flow-model-count"
        />
        <Column
          field="tags"
          title="Tags"
          sortable={false}
          className="column-tags"
          cells={{ data: TagsCell, filterCell: TagFilterCellMemo }}
        />
        <Column
          title="Actions"
          className="column-actions"
          width="100"
          sortable={false}
          filterable={false}
          cells={{ data: generateActionsCell }}
        />
      </KendoReactGrid>

      {editOpen && (
        <TagGroupEditModal
          id={selectedTagGroup.id}
          title={modalTitle}
          name={selectedTagGroup.name}
          description={selectedTagGroup.description}
          color={selectedTagGroup.color}
          tags={selectedTagGroup.tags}
          saveSuccess={() => handleSaveSuccess()}
          closeModal={() => closeEditModal()}
        />
      )}

      {deleteOpen && (
        <TagGroupDeleteModal
          id={selectedTagGroup.id}
          title="Delete Tag Group"
          name={selectedTagGroup.name}
          hasTagInUse={hasTagInUse}
          deleteSuccess={() => handleDeleteSuccess()}
          closeModal={() => setDeleteOpen(false)}
        />
      )}
    </div>
  );
};

export default SetupTagGroups;
